import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./document-krookie.module.scss";
import closeIcon from "../../images/master-plan-close.svg";
import { useNavigate } from "react-router-dom";
import { flushSync } from "react-dom";
import {
  CardKrookies,
  ChangeKrookieStatusPayload,
  DocumentOrigin,
  KrookieBackendPayload,
  KrookieStatus,
  KrookieStatusLabels,
} from "../../interfaces/krookie";
import { useAppSelector } from "../../hooks/useAppSelector";
import useGeneratePdf from "../../hooks/useGeneratePdf";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  fetchKrookieData,
  fetchMasterPlanModificationData,
  FormationTypeNames,
  KrookieFormationTypeId,
  setDrafter,
  setKrookiePlots,
  setResetKrookieData,
  setSelectedCity,
  setSelectedFormationType,
  setSelectedPlots,
  updateConvertedKrookieDataAsync,
} from "../../services/reducers/master-plan-modification";
import { LandPlot } from "../../interfaces/card";
import { changeKrookieStatus, GetKrookieDoc } from "../../services/api";
import {
  addPlotFromKrookieDataToArcgis,
  buildKrookieSerial,
  buildRevision,
  createLandPlot,
  createMergedKrookiePlot,
  deleteAndAddPlotsInArcgis,
  updatePlotFromKrookieData,
} from "../../utils/krookie-helpers";
import { fetchKrookies } from "../../services/reducers/table";
import { fetchAllArcgisObjects } from "../../services/reducers/filters";
import CardKrookie from "./card-krookie/card-krookie";
import {
  getEstateSerialForPlot,
  getPlotLocationLink,
} from "../../utils/verified-krookie-helpers";
import HistoryLog from "./history-log/history-log";
import KrookieActionPanel from "./krookie-action-panel/krookie-action-panel";
import { LayersDataTypes } from "../../types/layers-data-types";
import Polygon from "@arcgis/core/geometry/Polygon";
import { Roles } from "../../types/roles";
import warningIcon from "../../images/warning.svg";
import {
  appendKrookiesData,
  appendOldPlots,
  getStatusIcon,
  prepareKrookieApproveData,
  prepareKrookieData,
  processSaveResponse,
  updateKrookiesWithLatestComments,
} from "./krookie-handlers";

interface DocumentKrookie {
  setVisibleDocumentKrookie: (visible: boolean) => void;
  setVisibleModalSuccessful?: (visible: boolean) => void;
  setVisibleModalRejectKrookie?: (visible: boolean) => void;
  setVisibleModalApproveKrookie?: (visible: boolean) => void;
  data: Record<number, CardKrookies>;
  status: KrookieStatus;
  origin: DocumentOrigin;
  krookieFormationTypeId: KrookieFormationTypeId;
  onToggleCloseMasterPlanModal?: () => void;
}

const DocumentKrookie: FC<DocumentKrookie> = ({
  setVisibleDocumentKrookie,
  setVisibleModalSuccessful,
  setVisibleModalApproveKrookie,
  setVisibleModalRejectKrookie,
  data,
  origin,
  status,
  krookieFormationTypeId,
  onToggleCloseMasterPlanModal,
}) => {
  const { t } = useTranslation();
  const { krookiePlots, logComment, convertedKrookieData, selectedPlots } =
    useAppSelector((state) => state.masterPlanModification);
  const plotsData = useAppSelector(
    (state) => state.masterPlanModification.data,
  );
  const { user } = useAppSelector((state) => state.user);
  const { layers } = useAppSelector((state) => state.map);

  const generatePdf = useGeneratePdf();
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [currentKrookieIndex, setCurrentKrookieIndex] = useState(0);
  const [savedKrookiesData, setSavedKrookiesData] = useState<
    KrookieBackendPayload[]
  >([]);
  const [savedKrookieKeys, setSavedKrookieKeys] = useState<Set<number>>(
    new Set(),
  );
  const [approvedKrookiesData, setApprovedKrookiesData] = useState<
    Map<number, ChangeKrookieStatusPayload>
  >(new Map());
  const [hasCriticalOperation, setHasCriticalOperation] = useState(false);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [estateSerials, setEstateSerials] = useState<Record<number, string>>(
    {},
  );
  const [locationLinks, setLocationLinks] = useState<Record<number, string>>(
    {},
  );
  const [isApproving, setIsApproving] = useState(false);

  const allKrookies = Object.values(data);
  const allKrookieKeys = Object.keys(data).map(Number);
  const currentKrookieKey = allKrookieKeys[currentKrookieIndex];
  const estateSerial = estateSerials[currentKrookieKey] ?? null;
  const baseCurrentKrookie = allKrookies[currentKrookieIndex];
  // The location QR belongs only on the final, approved document. The
  // approved PDF is captured just before the status flips to Approved, so
  // the QR is also rendered for the duration of that capture (isApproving) —
  // never while the document is merely awaiting approval.
  const showLocationQr = status === KrookieStatus.Approved || isApproving;
  // Show the estate serial (from the polygon containing the plot centre),
  // prefixed with the plot number, in the document so both the on-screen
  // view and the printed PDF stay in sync.
  const currentKrookie = baseCurrentKrookie
    ? {
        ...baseCurrentKrookie,
        locationQrLink: showLocationQr
          ? locationLinks[currentKrookieKey]
          : undefined,
        // The "Edit documents" QR (shown pre-approval) only ever applies to
        // the Edit flow, where the plot already exists in the database.
        // locationLinks is computed for every krookie regardless of status —
        // including a brand-new Create-flow plot, whose geometry is enough
        // for getPlotLocationLink to build a (fid-less) fallback link even
        // though the plot hasn't been saved anywhere yet. Reusing that link
        // unconditionally made the QR appear during Create, before approval.
        // Restrict it back to Edit, matching the original synchronous
        // qrCodeLinkValue gating in master-plan-modification.ts.
        qrCodeLink:
          krookieFormationTypeId === KrookieFormationTypeId.Edit
            ? (locationLinks[currentKrookieKey] ?? baseCurrentKrookie.qrCodeLink)
            : baseCurrentKrookie.qrCodeLink,
        serial: estateSerial
          ? buildKrookieSerial(estateSerial, baseCurrentKrookie.plotNumber)
          : baseCurrentKrookie.serial,
      }
    : baseCurrentKrookie;
  const currentConvertedKrookieData =
    convertedKrookieData.krookies[currentKrookieKey];
  const hasMultipleKrookies = allKrookies.length > 1;
  const isCurrentKrookieSaved = savedKrookieKeys.has(currentKrookieKey);
  const unsavedKrookiesCount = allKrookieKeys.filter(
    (key) => !savedKrookieKeys.has(key),
  ).length;
  const isLastUnsavedKrookie =
    unsavedKrookiesCount === 1 && !isCurrentKrookieSaved;
  const currentDocumentUrl = currentConvertedKrookieData?.documentImage ?? "";
  const currentKrookiePlot = krookiePlots.find(
    (plot) => plot.objectId === currentKrookieKey,
  );
  const showApprovalAlert =
    status === KrookieStatus.NeedApproval &&
    user?.roles.includes(Roles.KrookieApprover) &&
    hasMultipleKrookies;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        krookiePlots.map(async (plot) => {
          const serial = await getEstateSerialForPlot(plot.newGeometry, layers);
          return [plot.objectId, serial] as const;
        }),
      );
      if (cancelled) return;
      const serialsMap: Record<number, string> = {};
      for (const [objectId, serial] of entries) {
        if (objectId != null && serial) serialsMap[objectId] = serial;
      }
      setEstateSerials(serialsMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [krookiePlots, layers]);

  useEffect(() => {
    let cancelled = false;
    const plotsLayerIds = layers
      .filter((layer) => layer.dataType === LayersDataTypes.Plots)
      .map((layer) => layer.id);
    (async () => {
      // In the creation/edit wizard the geometry comes from krookiePlots;
      // when the document is opened from the Journal (e.g. to approve it)
      // krookiePlots is empty, so fall back to the geometry saved with the
      // krookie itself.
      const entries = await Promise.all(
        Object.keys(data)
          .map(Number)
          .map(async (key) => {
            const savedGeometry = convertedKrookieData.krookies[key]?.geometry;
            const matchingPlot = krookiePlots.find(
              (plot) => plot.objectId === key,
            );
            const geometry =
              matchingPlot?.newGeometry ??
              (savedGeometry
                ? new Polygon(savedGeometry as __esri.PolygonProperties)
                : null);
            const link = await getPlotLocationLink(
              geometry,
              matchingPlot?.fid,
              plotsLayerIds,
            );
            return [key, link] as const;
          }),
      );
      if (cancelled) return;
      const linksMap: Record<number, string> = {};
      for (const [objectId, link] of entries) {
        if (objectId != null && link) linksMap[objectId] = link;
      }
      setLocationLinks(linksMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [data, krookiePlots, convertedKrookieData, layers]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasCriticalOperation) {
        event.preventDefault();
        return true;
      }
    };

    if (hasCriticalOperation) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasCriticalOperation]);

  const clearKrookieStates = () => {
    setSavedKrookieKeys(new Set());
    setApprovedKrookiesData(new Map());
    setSavedKrookiesData([]);
  };

  const handleSaveKrookieAndSeeNext = async (
    statusId: KrookieStatus | undefined = undefined,
  ) => {
    const krookieData = await prepareKrookieData(
      statusId,
      pdfContainerRef.current,
      generatePdf,
      currentKrookie,
      currentConvertedKrookieData,
      logComment,
      currentKrookiePlot,
      currentKrookieKey,
      estateSerial,
    );
    if (krookieData) {
      setSavedKrookiesData((prev) => {
        const newData = [...prev];
        newData[currentKrookieIndex] = krookieData;
        return newData;
      });
      setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
      navigateKrookie("next");
    }
  };

  const handleNewKrookie = async (
    formData: FormData,
    krookiesDataToBackEnd: KrookieBackendPayload[],
    krookieFormationTypeId: KrookieFormationTypeId,
  ) => {
    formData.append(
      "KrookieFormationTypeId",
      krookieFormationTypeId.toString(),
    );
    appendKrookiesData(formData, krookiesDataToBackEnd);
    appendOldPlots(formData, selectedPlots);
    await processSaveResponse(
      formData,
      setVisibleDocumentKrookie,
      setVisibleModalSuccessful,
    );
  };

  const handleUpdateKrookie = async (
    formData: FormData,
    krookiesDataToBackEnd: KrookieBackendPayload[],
  ) => {
    if (!currentConvertedKrookieData) return;
    formData.append("Id", currentConvertedKrookieData.sessionId.toString());
    appendKrookiesData(formData, krookiesDataToBackEnd);
    await processSaveResponse(
      formData,
      setVisibleDocumentKrookie,
      setVisibleModalSuccessful,
    );
  };

  const handleSaveKrookie = async (
    statusId: KrookieStatus | undefined = undefined,
  ) => {
    if (isRequestInProgress) return;
    setIsRequestInProgress(true);

    try {
    const currentKrookieData = await prepareKrookieData(
      statusId,
      pdfContainerRef.current,
      generatePdf,
      currentKrookie,
      currentConvertedKrookieData,
      logComment,
      currentKrookiePlot,
      currentKrookieKey,
      estateSerial,
    );
    if (!currentKrookieData) return;

    const allKrookiesData = [...savedKrookiesData];
    allKrookiesData[currentKrookieIndex] = currentKrookieData;

    const updatedKrookiesData = updateKrookiesWithLatestComments(
      allKrookiesData,
      allKrookieKeys,
      logComment,
    );

    const formData = new FormData();

    if (!currentConvertedKrookieData) {
      await handleNewKrookie(
        formData,
        updatedKrookiesData,
        krookieFormationTypeId,
      );
    } else {
      await handleUpdateKrookie(formData, updatedKrookiesData);
    }

    clearKrookieStates();
    } finally {
      setIsRequestInProgress(false);
    }
  };

  const handlePrintKrookie = async () => {
    await GetKrookieDoc(currentConvertedKrookieData.id);
  };

  const handleReturnBackToJournal = () => {
    setVisibleDocumentKrookie(false);
    if (origin === DocumentOrigin.Journal) {
      dispatch(setResetKrookieData());
    }
  };

  const handleEditKrookie = () => {
    if (!convertedKrookieData) return;
    const selectedPlots: LandPlot[] = [];

    if (convertedKrookieData.oldPlots.length > 0) {
      convertedKrookieData.oldPlots.forEach((oldPlot) => {
        const foundPlot = plotsData.find(
          (plot: LandPlot) => plot.objectId === oldPlot.plotId,
        );
        if (foundPlot) {
          selectedPlots.push(foundPlot);
        }
      });
    }

    switch (krookieFormationTypeId) {
      case KrookieFormationTypeId.Create: {
        const objectId = Number(Object.keys(convertedKrookieData.krookies)[0]);
        const baseEmptyPlot = createLandPlot({
          geometry: currentConvertedKrookieData.geometry,
          number: currentConvertedKrookieData.plotNumber,
          estateEn: currentConvertedKrookieData.cityEn.value ?? 0,
          estateAr: currentConvertedKrookieData.cityAr,
          landUse: currentConvertedKrookieData.landUse.value ?? 0,
          landUseId: currentConvertedKrookieData.landUse.value ?? 0,
          phase: currentConvertedKrookieData.phase.value ?? 0,
          phaseId: currentConvertedKrookieData.phase.value ?? 0,
          area: currentConvertedKrookieData.area,
          objectId,
        });

        dispatch(setKrookiePlots([baseEmptyPlot]));
        break;
      }

      case KrookieFormationTypeId.Edit: {
        const lastOldPlot =
          convertedKrookieData.oldPlots[
            convertedKrookieData.oldPlots.length - 1
          ];
        const foundPlot: LandPlot = plotsData.find(
          (plot: LandPlot) => plot.objectId === lastOldPlot.plotId,
        );
        const objectId = Number(Object.keys(convertedKrookieData.krookies)[0]);
        if (foundPlot) {
          const editedPlot = {
            ...foundPlot,
            number: currentConvertedKrookieData.plotNumber,
            geometry: new Polygon(currentConvertedKrookieData.geometry),
            objectId,
          };
          dispatch(setKrookiePlots([editedPlot]));
        }
        break;
      }

      case KrookieFormationTypeId.Merge: {
        const baseMergedPlot = createMergedKrookiePlot(selectedPlots);
        const objectId = Number(Object.keys(convertedKrookieData.krookies)[0]);

        if (baseMergedPlot) {
          const mergedPlot = {
            ...baseMergedPlot,
            number: currentConvertedKrookieData.plotNumber,
            objectId,
            geometry: currentConvertedKrookieData.geometry,
            estateEn: currentConvertedKrookieData.cityEn.value ?? 0,
            estateAr: currentConvertedKrookieData.cityAr,
            landUse: currentConvertedKrookieData.landUse.value ?? 0,
            landUseId: currentConvertedKrookieData.landUse.value ?? 0,
            phase: currentConvertedKrookieData.phase.value ?? 0,
            phaseId: currentConvertedKrookieData.phase.value ?? 0,
            area: currentConvertedKrookieData.area,
          };
          dispatch(setKrookiePlots([mergedPlot]));
        }
        break;
      }

      case KrookieFormationTypeId.Divide: {
        const dividedPlots: LandPlot[] = [];

        Object.entries(convertedKrookieData.krookies).forEach(
          ([objectId, krookieData]) => {
            const baseDividedPlot = createLandPlot({
              geometry: krookieData.geometry,
              number: krookieData.plotNumber,
              estateEn: krookieData.cityEn.value ?? 0,
              estateAr: krookieData.cityAr,
              landUse: krookieData.landUse.value ?? 0,
              landUseId: krookieData.landUse.value ?? 0,
              phase: krookieData.phase.value ?? 0,
              phaseId: krookieData.phase.value ?? 0,
              area: krookieData.area,
              objectId: Number(objectId),
            });
            dividedPlots.push(baseDividedPlot);
          },
        );

        dispatch(setKrookiePlots(dividedPlots));
        break;
      }
    }

    if (selectedPlots.length > 0) {
      dispatch(setSelectedPlots(selectedPlots));
    }
    dispatch(setSelectedCity(currentConvertedKrookieData.cityEn.value));
    dispatch(setDrafter({ name: user?.userName || "", date: new Date() }));

    switch (krookieFormationTypeId) {
      case KrookieFormationTypeId.Create: {
        dispatch(setSelectedFormationType(FormationTypeNames.Create));
        navigate("/masterPlan/create");
        break;
      }

      case KrookieFormationTypeId.Edit: {
        dispatch(setSelectedFormationType(FormationTypeNames.Edit));
        navigate("/masterPlan/edit");
        break;
      }

      case KrookieFormationTypeId.Merge: {
        dispatch(setSelectedFormationType(FormationTypeNames.Merge));
        navigate("/masterPlan/merge");
        break;
      }

      case KrookieFormationTypeId.Divide: {
        dispatch(setSelectedFormationType(FormationTypeNames.Divide));
        navigate("/masterPlan/divide");
        break;
      }
    }
  };

  // Captures the approval PDF with the location QR rendered in the card.
  const captureApprovalData = async (
    ...args: Parameters<typeof prepareKrookieApproveData>
  ) => {
    flushSync(() => setIsApproving(true));
    try {
      return await prepareKrookieApproveData(...args);
    } finally {
      setIsApproving(false);
    }
  };

  const handleApproveKrookieAndSeeNext = async () => {
    if (!convertedKrookieData || !currentConvertedKrookieData) return;

    const approvalData = await captureApprovalData(
      currentKrookieKey,
      currentConvertedKrookieData,
      user,
      dispatch,
      pdfContainerRef.current,
      generatePdf,
    );
    if (!approvalData) {
      return;
    }

    setApprovedKrookiesData((prev) => {
      const newMap = new Map(prev);
      newMap.set(currentKrookieKey, approvalData);
      return newMap;
    });

    setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
    navigateKrookie("next");
  };

  const handleApproveKrookie = async () => {
    if (!convertedKrookieData) return;
    if (isRequestInProgress) return;
    setIsRequestInProgress(true);

    setHasCriticalOperation(true);

    const localApprovalData = new Map(approvedKrookiesData);
    const isCreate = krookieFormationTypeId === KrookieFormationTypeId.Create;
    const isMerge = krookieFormationTypeId === KrookieFormationTypeId.Merge;
    const isDivide = krookieFormationTypeId === KrookieFormationTypeId.Divide;

    try {
      let currentApprovalData = localApprovalData.get(currentKrookieKey);
      if (!currentApprovalData) {
        currentApprovalData = await captureApprovalData(
          currentKrookieKey,
          currentConvertedKrookieData,
          user,
          dispatch,
          pdfContainerRef.current,
          generatePdf,
        );
        if (!currentApprovalData) return;
        localApprovalData.set(currentKrookieKey, currentApprovalData);
        // Persist right away (not just in the local map above) so that if
        // a later step below (e.g. the ArcGIS write) fails and the user
        // retries, this same revision/PDF is reused instead of
        // captureApprovalData running again — which bumps the revision
        // number and adds another history-log entry every retry.
        const capturedApprovalData = currentApprovalData;
        setApprovedKrookiesData((prev) => {
          const newMap = new Map(prev);
          newMap.set(currentKrookieKey, capturedApprovalData);
          return newMap;
        });
      }

      const oldPlots = convertedKrookieData.oldPlots;
      const oldPlotsIds = oldPlots.map((plot) => plot.plotId);

      if (isDivide || isMerge) {
        const krookiesToAdd = allKrookieKeys.map(
          (key) => convertedKrookieData.krookies[key],
        );
        const newObjectIds = await deleteAndAddPlotsInArcgis(
          layers,
          oldPlotsIds,
          krookiesToAdd,
          isMerge,
        );
        newObjectIds.forEach((newObjectId, index) => {
          const krookieKey = allKrookieKeys[index];
          if (newObjectId) {
            const existingData = localApprovalData.get(krookieKey);
            if (existingData) {
              localApprovalData.set(krookieKey, {
                ...existingData,
                newPlotId: newObjectId.toString(),
              });
            }
          }
        });
      } else {
        for (const krookieKey of allKrookieKeys) {
          const currentKrookie = convertedKrookieData.krookies[krookieKey];
          let newObjectId: number | null = null;
          if (isCreate) {
            newObjectId = await addPlotFromKrookieDataToArcgis(
              layers,
              currentKrookie,
            );
          } else {
            await updatePlotFromKrookieData(layers, currentKrookie, oldPlots);
          }
          if (newObjectId) {
            const existingData = localApprovalData.get(krookieKey);
            if (existingData) {
              localApprovalData.set(krookieKey, {
                ...existingData,
                newPlotId: newObjectId.toString(),
              });
            }
          }
        }
      }

      const finalApprovalData = Array.from(localApprovalData.values()).map(
        (approvalData) => ({
          krookieId: approvalData.krookieId,
          changeNum: approvalData.changeNum,
          documentBody: approvalData.documentBody,
          ...(approvalData.newPlotId && { newPlotId: approvalData.newPlotId }),
        }),
      );

      await changeKrookieStatus(KrookieStatus.Approved, finalApprovalData);

      await dispatch(
        fetchKrookieData({
          sessionId: currentConvertedKrookieData.sessionId,
          status: KrookieStatusLabels[KrookieStatus.Approved],
          krookieId: currentConvertedKrookieData.id,
        }),
      );

      await Promise.all([
        dispatch(fetchAllArcgisObjects()),
        dispatch(fetchMasterPlanModificationData()),
      ]);

      setVisibleModalApproveKrookie?.(true);
      clearKrookieStates();
      dispatch(fetchKrookies());
    } catch (error) {
      console.error(error);
    } finally {
      setIsRequestInProgress(false);
      setHasCriticalOperation(false);
    }
  };

  const handleSubmitForApprovalAndSeeNext = async () => {
    if (!convertedKrookieData) return;
    setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
    navigateKrookie("next");
  };

  const handleSubmitForApproval = async () => {
    if (!convertedKrookieData) return;
    if (isRequestInProgress) return;
    setIsRequestInProgress(true);

    try {
      const krookiesToSubmit = allKrookieKeys.filter(
        (key) => savedKrookieKeys.has(key) || key === currentKrookieKey,
      );

      if (!savedKrookieKeys.has(currentKrookieKey)) {
        setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
      }

      for (const krookieKey of krookiesToSubmit) {
        const krookieData = convertedKrookieData.krookies[krookieKey];
        if (!krookieData) continue;

        const { revision, changeNum } = buildRevision(krookieData.historyLog);

        await dispatch(
          updateConvertedKrookieDataAsync({
            id: krookieKey,
            data: {
              revision: revision,
            },
          }),
        );

        await changeKrookieStatus(KrookieStatus.NeedApproval, [
          {
            krookieId: krookieData.id.toString(),
            changeNum,
          },
        ]);

        await dispatch(
          fetchKrookieData({
            sessionId: krookieData.sessionId,
            status: KrookieStatusLabels[KrookieStatus.NeedApproval],
            krookieId: krookieData.id,
          }),
        );
      }

      clearKrookieStates();
      dispatch(fetchKrookies());
    } catch (error) {
      console.error(error);
    } finally {
      setIsRequestInProgress(false);
    }
  };

  const handleRejectKrookie = async () => {
    if (!convertedKrookieData) return;
    if (isRequestInProgress) return;
    setIsRequestInProgress(true);
    try {
      for (const krookieKey of allKrookieKeys) {
        const krookieData = convertedKrookieData.krookies[krookieKey];

        const { revision, changeNum, currentDate } = buildRevision(
          krookieData.historyLog,
        );
        await dispatch(
          updateConvertedKrookieDataAsync({
            id: krookieKey,
            data: {
              revision: revision,
              signatureDrafter: currentDate,
            },
          }),
        );

        await changeKrookieStatus(KrookieStatus.Rejected, [
          {
            krookieId: krookieData.id.toString(),
            changeNum,
          },
        ]);
        await dispatch(
          fetchKrookieData({
            sessionId: krookieData.sessionId,
            status: KrookieStatusLabels[KrookieStatus.Rejected],
            krookieId: krookieData.id,
          }),
        );
      }

      clearKrookieStates();
      dispatch(fetchKrookies());
      if (setVisibleModalRejectKrookie) {
        setVisibleModalRejectKrookie(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRequestInProgress(false);
    }
  };

  const handleCloseDocument = () => {
    if (onToggleCloseMasterPlanModal) {
      onToggleCloseMasterPlanModal();
    } else {
      setVisibleDocumentKrookie(false);
    }

    if (origin === DocumentOrigin.Journal) {
      dispatch(setResetKrookieData());
    }
  };

  const navigateKrookie = (direction: "next" | "prev") => {
    setCurrentKrookieIndex((prevIndex) => {
      if (direction === "next") {
        return (prevIndex + 1) % allKrookies.length;
      } else {
        return (prevIndex - 1 + allKrookies.length) % allKrookies.length;
      }
    });
  };

  return (
    <div className={styles.modalOverlay}>
      <div key={currentKrookieKey} className={styles.document}>
        <div className={styles.header}>
          <div className={styles.title}>
            <span>
              {t("document-krookie.krookiePlotNumber")}:{" "}
              {origin === DocumentOrigin.Journal
                ? currentKrookie?.plotNumber
                : currentKrookie?.plotNumber}
            </span>
            <p>
              <img
                className={styles.statusImg}
                src={getStatusIcon(status)}
                alt={KrookieStatusLabels[status]}
              />{" "}
              {KrookieStatusLabels[status]}
            </p>
            {hasMultipleKrookies && (
              <span className={styles.countKrookies}>
                {t("document-krookie.krookieCounter", {
                  current: currentKrookieIndex + 1,
                  total: allKrookies.length,
                })}
              </span>
            )}
          </div>
          <img
            className={styles.closeIcon}
            onClick={handleCloseDocument}
            src={closeIcon}
            alt="close icon"
          />
        </div>
        <div className={styles.body}>
          <div className={styles.cardKrookie}>
            {currentDocumentUrl ? (
              <img
                className={styles.documentImage}
                src={currentDocumentUrl}
                alt="Document Krookie"
              />
            ) : (
              <>
                {/* Editing preview: page 1 only, even for a long
                    coordinates table that spans two pages — the full
                    document (both pages) is only ever built off-screen,
                    right before printing/saving, from the render below. */}
                <CardKrookie data={currentKrookie} previewOnly />
                <div
                  ref={pdfContainerRef}
                  style={{
                    position: "fixed",
                    top: "-10000px",
                    left: "-10000px",
                    pointerEvents: "none",
                  }}
                >
                  <CardKrookie data={currentKrookie} />
                </div>
              </>
            )}
          </div>
          <div className={styles.rightColumn}>
            <HistoryLog
              documentId={Number(currentKrookieKey)}
              origin={origin}
            />
            {showApprovalAlert && (
              <div className={styles.approvalAlert}>
                <img src={warningIcon} alt="alert" />
                <span>{t("document-krookie.approvalWarning")}</span>
              </div>
            )}
            <KrookieActionPanel
              origin={origin}
              status={status}
              onEdit={handleEditKrookie}
              onApprove={handleApproveKrookie}
              onReject={handleRejectKrookie}
              onSubmitForApproval={handleSubmitForApproval}
              onReturn={handleReturnBackToJournal}
              onPrint={handlePrintKrookie}
              onSave={handleSaveKrookie}
              convertedKrookieData={currentConvertedKrookieData}
              krookieFormationTypeId={krookieFormationTypeId}
              onSaveAndSeeNext={handleSaveKrookieAndSeeNext}
              hasMultipleKrookies={hasMultipleKrookies}
              isLastUnsavedKrookie={isLastUnsavedKrookie}
              onSubmitForApprovalAndSeeNext={handleSubmitForApprovalAndSeeNext}
              onApproveAndSeeNext={handleApproveKrookieAndSeeNext}
              isRequestInProgress={isRequestInProgress}
            />
          </div>
        </div>
        {hasMultipleKrookies && (
          <>
            <button
              className={`${styles.navButton} ${styles.prevBtn}`}
              onClick={() => navigateKrookie("prev")}
              type="button"
            >
              <span className={styles.img} />
            </button>
            <button
              className={`${styles.navButton} ${styles.nextBtn}`}
              onClick={() => navigateKrookie("next")}
              type="button"
            >
              <span className={styles.img} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentKrookie;
